/**
 * Thin fetch wrapper for the helpdesk API. Errors always arrive as
 * { message, fields? } and are thrown as ApiError so forms can show
 * field messages inline.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly fields: Record<string, string>;

  constructor(status: number, message: string, fields: Record<string, string> = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.fields = fields;
  }
}

/** JSON-serialisable object or array, or a FormData for uploads. */
type Body = object | null;

// ── Reachability: whether the last request got through ──────────────
// navigator.onLine only knows about the device's own connection; this also
// catches "online, but the helpdesk server can't be reached".
let reachable = true;
const reachabilityListeners = new Set<() => void>();
export function setReachable(value: boolean) {
  if (value === reachable) return;
  reachable = value;
  reachabilityListeners.forEach((l) => l());
}
export const isReachable = () => reachable;
export function onReachabilityChange(listener: () => void) {
  reachabilityListeners.add(listener);
  return () => void reachabilityListeners.delete(listener);
}

async function request<T>(method: string, path: string, body?: Body): Promise<T> {
  const init: RequestInit = { method, credentials: 'same-origin', headers: {} };
  if (body instanceof FormData) {
    init.body = body;
  } else if (body !== undefined) {
    init.body = JSON.stringify(body);
    (init.headers as Record<string, string>)['Content-Type'] = 'application/json';
  }

  let res: Response;
  try {
    res = await fetch(path, init);
  } catch {
    setReachable(false);
    throw new ApiError(0, 'Can’t reach the helpdesk. Check your connection and try again.');
  }
  setReachable(true);
  if (res.status === 204) return undefined as T;

  const data = await res.json().catch(() => null);
  if (!res.ok) {
    throw new ApiError(res.status, data?.message ?? `Request failed (${res.status}).`, data?.fields ?? {});
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: Body) => request<T>('POST', path, body ?? {}),
  put: <T>(path: string, body: Body) => request<T>('PUT', path, body),
  patch: <T>(path: string, body: Body) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};

/** Build a query string, skipping empty values. */
export function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') search.set(k, String(v));
  }
  const s = search.toString();
  return s ? `?${s}` : '';
}
